import { useFetch, apiPut, timeAgo } from "@/lib/shieldx";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { StatusBadge, DemoTag } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import { Plug, Check, X } from "lucide-react";
import { toast } from "sonner";

const FLOW = ["Discovered", "Verify", "Access Request", "Admin Review", "Approve / Deny", "Connect", "Monitor"];

export default function Integrations() {
  const { data, loading, reload } = useFetch("/integrations");
  if (loading || !data) return <Loader label="Loading integration registry" />;

  const decide = async (id, decision) => {
    try { await apiPut(`/integrations/${id}/trust`, { decision }); toast.success(`Integration ${decision === "APPROVE" ? "approved & connected" : "denied"}`); reload(); }
    catch { toast.error("Action failed"); }
  };

  const columns = [
    { key: "name", label: "Integration", render: (r) => (
      <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.category}</p></div>
    ) },
    { key: "health", label: "Health", render: (r) => <span className={r.health === "Healthy" ? "text-emerald-400" : "text-slate-400"}>{r.health}</span> },
    { key: "discovered_at", label: "Discovered", render: (r) => <span className="text-xs text-slate-500">{timeAgo(r.discovered_at)}</span> },
    { key: "status", label: "Trust State", render: (r) => <StatusBadge value={r.status} /> },
    { key: "actions", label: "", render: (r) => (
      ["PENDING", "UNTRUSTED"].includes(r.status) ? (
        <div className="flex gap-2">
          <button onClick={() => decide(r.id, "APPROVE")} data-testid={`integration-approve-${r.id}`}
            className="flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20"><Check className="h-3.5 w-3.5" /> Approve</button>
          <button onClick={() => decide(r.id, "DENY")} data-testid={`integration-deny-${r.id}`}
            className="flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1 text-xs font-medium text-slate-300 hover:bg-slate-800"><X className="h-3.5 w-3.5" /> Deny</button>
        </div>
      ) : <span className="font-mono text-[11px] text-slate-600">—</span>
    ) },
  ];

  return (
    <div>
      <PageHeader icon={Plug} title="Integration Registry"
        subtitle="New integrations always start UNTRUSTED / PENDING. Trust is earned through explicit admin review — never automatic." actions={<DemoTag />} />
      <Panel title="Trust Workflow" className="mb-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {FLOW.map((f, i) => (
            <span key={f} className="flex items-center gap-1.5">
              <span className="rounded border border-slate-700 bg-slate-800/60 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-slate-300">{f}</span>
              {i < FLOW.length - 1 && <span className="text-slate-600">→</span>}
            </span>
          ))}
        </div>
      </Panel>
      <Table columns={columns} rows={data} testid="integrations-table" />
    </div>
  );
}
